import { withTranslate } from "@pathwright/ui/src/components/"
import Button from "@pathwright/ui/src/components/button/Button"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ui/src/components/tooltip/Tooltip"
import Text from "@pathwright/ui/src/components/ui/Text"
import View from "@pathwright/ui/src/components/ui/View"
import formatStripeAmount from "@pathwright/web/src/modules/stripe/utils/formatStripeAmount"
import getStartingStripePrice from "@pathwright/web/src/modules/stripe/utils/getStartingStripePrice"
import getStripePriceIntervalLabel from "@pathwright/web/src/modules/stripe/utils/getStripePriceIntervalLabel"
import getStripePriceTotalAmount from "@pathwright/web/src/modules/stripe/utils/getStripePriceTotalAmount"
import { getIsSubscriptionUsable } from "@pathwright/web/src/modules/subscription/utils"
import { withUserCommunity } from "@pathwright/web/src/modules/user/UserCommunityContext"
import ConnectStores from "lib/core/store/components/ConnectStores"
import {
  OFFERING_OPTION_IN_PLAN,
  OFFERING_OPTION_NOT_IN_PLAN
} from "library/register/constants"
import get from "lodash/get"
import moment from "moment"
import { useEffect, useRef, useState } from "react"
import OrderRegistrationButton from "../../../../register/components/OrderRegistrationButton"
import ResourceOfferingForm from "./ResourceOfferingForm"

const tPrefix = "resource.offering_form"

const ResourceOfferingFormContainer = ({
  selection: selectionProp,
  registerTo,
  registration,
  offering,
  offeringOption,
  planOption,
  planOptions,
  shouldShowOptions,
  isIncludedInUserSubscription,
  isIncludedInGroupSubscription,
  hasMultipleOptions,
  resource_type,
  t
}) => {
  const [selection, setSelection] = useState(selectionProp)
  // HACK: allows for showing the "Start course" button immediately after user registers
  const hadAccess = useRef(
    !!offering.permissions && !!offering.permissions.can_access
  ).current

  useEffect(() => {
    setSelection(selectionProp)
  }, [selectionProp])

  const buttonProps = {
    to: selection
      ? selection.option
        ? {
            ...registerTo,
            query: {
              ...registerTo.query,
              ro: selection.option
            }
          }
        : registerTo
      : registerTo
  }

  return (
    <View padding borderBottom className="ResourceOffering__Form">
      {shouldShowOptions && (
        <ResourceOfferingForm
          offeringOption={offeringOption}
          planOptions={planOptions}
          resource_type={resource_type}
          isIncludedInUserSubscription={isIncludedInUserSubscription}
          isIncludedInGroupSubscription={isIncludedInGroupSubscription}
          hasMultipleOptions={hasMultipleOptions}
          planOption={planOption}
          onSelect={setSelection}
          selection={selection}
        />
      )}
      {hadAccess && offering.permissions && offering.permissions.can_access ? (
        <Text.H5>{t(`${tPrefix}.you_are_registered`)}.</Text.H5>
      ) : moment().isBefore(offering.enrollment_starts) ? (
        <Text.H5>
          <Tooltip
            title={`${t(`${tPrefix}.opens`)} ${moment(
              offering.enrollment_starts
            ).format("LLL")}`}
          >
            {t(`${tPrefix}.registration_open`)}{" "}
            {moment(offering.enrollment_starts).fromNow()}
            {`.`}
          </Tooltip>
        </Text.H5>
      ) : moment().isAfter(offering.enrollment_ends) ? (
        <Text.H5>
          <Tooltip
            title={`Closes ${moment(offering.enrollment_ends).format("LLL")}`}
          >
            {t(`${tPrefix}.registration_closed`)}{" "}
            {moment(offering.enrollment_ends).fromNow()}
            {`.`}
          </Tooltip>
        </Text.H5>
      ) : selection && selection.action ? (
        <OrderRegistrationButton
          offering={offering}
          registration={registration}
          offering_option={selection.option}
          orderData={{
            offering_id: offering.id
          }}
          submitLabel={
            <>
              {selection.action} <Pathicon icon="chevron-right" />
            </>
          }
        >
          <Button {...buttonProps} styleType="primary" brand>
            {selection.action} <Pathicon icon="chevron-right" />
          </Button>
        </OrderRegistrationButton>
      ) : // No option is avaliable. Likely the offering is included in a subscription that is "hidden"
      // and must be revealed by a visibility code
      null}
    </View>
  )
}

ResourceOfferingFormContainer.displayName = "ResourceOfferingFormContainer"

const mapStateToProps = function (state, ownProps) {
  let registerTo
  const { offering, communityGroups, hasActiveMembership, t } = ownProps
  const { resource } = state.resourceStore.state
  const { subscription_plans: plans1 } = state.schoolStore.state
  const { plans: plans2 } = state.subscribeStore.state
  const { subscription } = state.subscribeStore.state
  const { offering_option } = state.registerStore.state
  const { location } = state.navigationStore.state

  // Merge subscription_plans with user's subscription.school_plan to
  // ensure we have all possible plans (the user's subscription.school_plan might not be "visible", not present in subscription_plans)
  const subscription_plans = []
    .concat(plans1 || [])
    .concat(plans2 || [])
    .concat(
      subscription && subscription.school_plan ? subscription.school_plan : []
    )
    .reduce((plans, plan) => {
      plans = [].concat(plans)
      return !plans.find((p) => p.id == plan.id) ? plans.concat(plan) : plans
    }, [])

  // Have to make sure we get a registration for the offering we're looking at
  const registration = state.resourceStore.request.getRegistrationForOffering(
    offering.id
  )

  const isIncludedInUserSubscription =
    state.resourceStore.request.includedInUserSubscription(offering).length > 0

  let includedInProductIds = offering.include_with_products_data
    ? offering.include_with_products_data.map((x) => x.id)
    : []
  let isIncludedInGroupSubscription = false
  let isMultiSeatGroupSubscription = false
  // check any community groups user is member of
  communityGroups.forEach((communityGroup) => {
    communityGroup.group_subscriptions.forEach((groupSubscription) => {
      if (
        // if group subscription is to a product that includes this offering
        includedInProductIds.includes(groupSubscription.price.product.id) &&
        // and the group subscription is active
        getIsSubscriptionUsable(groupSubscription.subscription) &&
        // and the user's membership is active
        hasActiveMembership(groupSubscription.community_group) &&
        groupSubscription.community_group.seats_available > -1
      ) {
        // then this offering is included in group subscription
        isIncludedInGroupSubscription = true
        if (groupSubscription.subscription.quantity > 1) {
          isMultiSeatGroupSubscription = true
        }
      }
    })
  })

  const hasMultipleOptions =
    !offering.resource &&
    (offering.include_in_subscription_plans.length > 0 ||
      offering.include_with_products.length > 0) &&
    !offering.is_subscription_only

  let resource_type = "course"
  if (resource.resource_type === 30) resource_type = "collection"
  if (resource.resource_type === 20) resource_type = "lesson"

  // show options if no registration for this offering
  const shouldShowOptions = !registration
  const hasMonthlyPlan = !!(
    subscription_plans &&
    subscription_plans.find(({ plan }) => plan.interval === 5)
  )

  const getDisplayInterval = function (interval) {
    switch (interval) {
      case 5:
        return t(`${tPrefix}.per_month`)
      case 10:
        return t(`${tPrefix}.per_year`)
      default:
        return ""
    }
  }

  const getOfferingDisplayPrice = function (offering) {
    if (offering.price === 0) {
      return t(`${tPrefix}.free`)
    } else {
      return offering.currency_display
    }
  }

  const getDisplayText = function (planOptions) {
    // registration.progress is 0, 1 or in between
    if (registration != null) {
      return (
        [t(`${tPrefix}.start`), t(`${tPrefix}.share`)][registration.progress] ||
        t(`${tPrefix}.continue`)
      )
    }
    if (
      !(planOptions != null ? planOptions.length : undefined) ||
      isIncludedInUserSubscription ||
      isIncludedInGroupSubscription
    ) {
      return `${t(`${tPrefix}.take`)}`
    }
    // if offering isn't included in user subscriptsion then "Subscribe" or "Learn More"
    if ((planOptions != null ? planOptions.length : undefined) > 1) {
      return `${t(`${tPrefix}.learn_more`)}`
    }
    return `${t(`${tPrefix}.subscribe`)}`
  }

  const base_url = `/library/${resource.slug}/`
  if (registration != null) {
    const pathOrAchievement = registration.progress < 1 ? "path" : "achievement"
    registerTo = {
      pathname: `${base_url}${offering.id}/${pathOrAchievement}/`,
      state: { modal: registration.progress === 1 },
      query: location.query
    }
  } else {
    registerTo = {
      state: { modal: true },
      pathname: `${base_url}register/${offering.id}/`,
      query: location.query
    }
  }

  const planOptions = []
  let planOption = null
  if (subscription_plans) {
    for (var p of Array.from(subscription_plans)) {
      if (_.includes(offering.include_in_subscription_plans, p.id)) {
        planOptions.push({
          amount: p.plan.amount,
          resources_count: p.resources_count,
          display_interval: getDisplayInterval(p.plan.interval),
          display_price: p.currency_display,
          option: OFFERING_OPTION_IN_PLAN
        })
      }
    }
  }
  // Add stripe products as if they were subscription plans.
  // Since the action button just opens the subscribe flow this works
  // despite being a different object under the hood
  if (get(offering, "include_with_products_data.length")) {
    offering.include_with_products_data.forEach((product) => {
      let startingPrice = getStartingStripePrice(product.stripe_prices)
      if (startingPrice) {
        const totalAmount = getStripePriceTotalAmount(startingPrice, 1)
        const intervalLabel = getStripePriceIntervalLabel(
          startingPrice,
          true,
          t
        )
        const formattedAmount = formatStripeAmount(
          totalAmount,
          startingPrice.currency
        )
        planOptions.push({
          amount: totalAmount,
          display_interval: intervalLabel,
          display_price: formattedAmount,
          resources_count: null,
          option: OFFERING_OPTION_IN_PLAN
        })
      }
    })
  }
  if (planOptions.length) {
    for (var p of Array.from(planOptions)) {
      p.action = getDisplayText(planOptions)
    }
    // Get the lowest costing plan
    // NOTE: ammount for yearly plans has been divided by 12 (making it likely less than the monthly plan(s))
    planOption = _.first(planOptions.sort((a, b) => a.amount - b.amount))
  }

  let offeringOption = null
  if (!offering.is_subscription_only) {
    offeringOption = {
      display_price: getOfferingDisplayPrice(offering),
      action: getDisplayText(),
      option: OFFERING_OPTION_NOT_IN_PLAN
    }
  }

  const selection = (() => {
    switch (offering_option) {
      case OFFERING_OPTION_IN_PLAN:
        return planOption
      case OFFERING_OPTION_NOT_IN_PLAN:
        return offeringOption
      // TODO: remove "or {}" when resource loader is fixed
      // prioritizing subscription option in app
      default:
        return offeringOption || planOption || {}
    }
  })()

  const props = _.assign(
    {},
    {
      offering,
      registration,
      offeringOption,
      registerTo,
      shouldShowOptions,
      planOptions,
      hasMultipleOptions,
      resource_type,
      planOption,
      offering_option,
      selection,
      isIncludedInUserSubscription,
      isIncludedInGroupSubscription,
      isMultiSeatGroupSubscription,
      t
    }
  ) // decorating ownProps
  return props
}

const stores = [
  { store: "school", watch: false },
  { store: "subscribe", watch: ["subscribe_state", "plans"] },
  { store: "resource", watch: ["registration", "registrations", "offering"] },
  { store: "navigation", watch: ["location"] },
  { store: "register", watch: false }
]

export default withTranslate(
  withUserCommunity(
    ConnectStores(ResourceOfferingFormContainer, stores, {}, mapStateToProps)
  )
)
